/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { dateFormat } from "~/constants/dateFormats";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import { getCountBasedDisplayValue } from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { ResourceQuery } from "../types";

type DefinitionKey =
  | "cloudServices"
  | "regions"
  | "resourceTypes"
  | "cloudProviders"
  | "cloudAccounts"
  | "discoveryTime"
  | "resourceTags"
  | "resourceMetadata"
  | "resourceLifeSpan"
  | "lastPatchingStatuses"
  | "allocation";

type ResourceQueryState = ResourceQuery & QueryState;

type Definition = AppliedFilterDefinition<ResourceQueryState, DefinitionKey>;

export const resourcesV2AppliedFilters: Definition[] = [
  createCountFilterDefinition<ResourceQueryState, "cloudServices">({
    key: "cloudServices",
    label: "Cloud Services",
    labels: { singular: "Cloud Service", plural: "Cloud Services" },
    getValues: (queryState) => queryState.cloudServices,
    getExcluded: (queryState) => queryState.excludeCloudServices,
    clear: () => ({ cloudServices: [], excludeCloudServices: false }),
    applyValuesPatch: (queryState, values) => ({
      cloudServices: values,
      excludeCloudServices: values.length
        ? queryState.excludeCloudServices
        : false,
    }),
  }),
  createCountFilterDefinition<ResourceQueryState, "regions">({
    key: "regions",
    label: "Region",
    labels: { singular: "Region", plural: "Regions" },
    getValues: (queryState) => queryState.regions,
    getExcluded: (queryState) => queryState.excludeRegions,
    clear: () => ({ regions: [], excludeRegions: false }),
    applyValuesPatch: (queryState, values) => ({
      regions: values,
      excludeRegions: values.length ? queryState.excludeRegions : false,
    }),
  }),
  createCountFilterDefinition<ResourceQueryState, "resourceTypes">({
    key: "resourceTypes",
    label: "Resource Type",
    labels: { singular: "Resource Type", plural: "Resource Types" },
    getValues: (queryState) => queryState.resourceTypes,
    getExcluded: (queryState) => queryState.excludeResourceTypes,
    clear: () => ({ resourceTypes: [], excludeResourceTypes: false }),
    applyValuesPatch: (queryState, values) => ({
      resourceTypes: values,
      excludeResourceTypes: values.length
        ? queryState.excludeResourceTypes
        : false,
    }),
  }),
  createCountFilterDefinition<ResourceQueryState, "cloudProviders">({
    key: "cloudProviders",
    label: "Cloud Provider",
    labels: { singular: "Cloud Provider", plural: "Cloud Providers" },
    getValues: (queryState) => queryState.cloudProviders,
    getExcluded: (queryState) => queryState.excludeCloudProviders,
    clear: () => ({ cloudProviders: [], excludeCloudProviders: false }),
    applyValuesPatch: (queryState, values) => ({
      cloudProviders: values,
      excludeCloudProviders: values.length
        ? queryState.excludeCloudProviders
        : false,
    }),
  }),
  createCountFilterDefinition<ResourceQueryState, "cloudAccounts">({
    key: "cloudAccounts",
    label: "Cloud Accounts",
    labels: { singular: "Cloud Account", plural: "Cloud Accounts" },
    getValues: (queryState) => queryState.cloudAccounts,
    getExcluded: (queryState) => queryState.excludeCloudAccounts,
    clear: () => ({ cloudAccounts: [], excludeCloudAccounts: false }),
    applyValuesPatch: (queryState, values) => ({
      cloudAccounts: values,
      excludeCloudAccounts: values.length
        ? queryState.excludeCloudAccounts
        : false,
    }),
  }),
  {
    key: "discoveryTime",
    label: "Discovery Time",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.discoveryTimeStartUTC && queryState.discoveryTimeEndUTC
        ? [queryState.discoveryTimeStartUTC, queryState.discoveryTimeEndUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.discoveryTimeStartUTC).format(
        dateFormat.shortDate
      )} - ${dayjs(queryState.discoveryTimeEndUTC).format(
        dateFormat.shortDate
      )}`,
    clear: () => ({
      discoveryTimeStartUTC: undefined,
      discoveryTimeEndUTC: undefined,
      excludeDiscoveryTimeRange: false,
    }),
  },
  createCountFilterDefinition<ResourceQueryState, "resourceTags">({
    key: "resourceTags",
    label: "Resource Tags",
    labels: { singular: "Resource Tag", plural: "Resource Tags" },
    getValues: (queryState) => queryState.resourceTagNamesAndTagValues,
    getExcluded: (queryState) => queryState.excludeResourceTagNamesAndTagValues,
    clear: () => ({
      resourceTagNamesAndTagValues: [],
      excludeResourceTagNamesAndTagValues: false,
    }),
    applyValuesPatch: (queryState, values) => ({
      resourceTagNamesAndTagValues: values,
      excludeResourceTagNamesAndTagValues: values.length
        ? queryState.excludeResourceTagNamesAndTagValues
        : false,
    }),
  }),
  createCountFilterDefinition<ResourceQueryState, "resourceMetadata">({
    key: "resourceMetadata",
    label: "Resource Metadata",
    labels: { singular: "Metadata Entry", plural: "Metadata Entries" },
    getValues: (queryState) => queryState.resourceMetadataKeysAndValues,
    getExcluded: (queryState) =>
      queryState.excludeResourceMetadataKeysAndValues,
    clear: () => ({
      resourceMetadataKeysAndValues: [],
      excludeResourceMetadataKeysAndValues: false,
    }),
    applyValuesPatch: (queryState, values) => ({
      resourceMetadataKeysAndValues: values,
      excludeResourceMetadataKeysAndValues: values.length
        ? queryState.excludeResourceMetadataKeysAndValues
        : false,
    }),
  }),
  {
    key: "resourceLifeSpan",
    label: "Resource Lifespan",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.resourceLifeSpanFrom !== undefined &&
      queryState.resourceLifeSpanTo !== undefined
        ? [
            `${queryState.resourceLifeSpanFrom}`,
            `${queryState.resourceLifeSpanTo}`,
          ]
        : [],
    getValueDisplay: (queryState) =>
      `${queryState.resourceLifeSpanFrom} - ${queryState.resourceLifeSpanTo}`,
    clear: () => ({
      resourceLifeSpanFrom: undefined,
      resourceLifeSpanTo: undefined,
      excludeResourceLifeSpan: false,
    }),
  },
  createCountFilterDefinition<ResourceQueryState, "lastPatchingStatuses">({
    key: "lastPatchingStatuses",
    label: "Last Patching Status",
    labels: {
      singular: "Last Patching Status",
      plural: "Last Patching Statuses",
    },
    getValues: (queryState) => queryState.lastPatchingStatuses,
    getExcluded: (queryState) => queryState.excludeLastPatchingStatuses,
    clear: () => ({
      lastPatchingStatuses: [],
      excludeLastPatchingStatuses: false,
    }),
    applyValuesPatch: (queryState, values) => ({
      lastPatchingStatuses: values,
      excludeLastPatchingStatuses: values.length
        ? queryState.excludeLastPatchingStatuses
        : false,
    }),
  }),
  {
    key: "allocation",
    label: "Allocation",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.allocation ? [queryState.allocation] : [],
    getValueDisplay: (_queryState, values) =>
      getCountBasedDisplayValue(values, false, {
        singular: "Allocation",
        plural: "Allocations",
      }),
    clear: () => ({ allocation: undefined }),
  },
];
